<template>
  <MessageOverlaySkeleton
    v-if="messages"
    class="gw_mesblock_container mso_circleshadows"
    style="
      z-index: 8;
      --opacity: 0.525;
      --back-from: rgba(0, 0, 0, var(--opacity));
      --back-to: rgba(0, 0, 0, var(--opacity));
      --content-margin: 0;

      --message-padding: 2rem;
      --clr-message-back: var(--clr-black-0-3);

      --size-title: 1.5rem;
      --weight-title: 600;

      --clr-sub: var(--clr-grey-2);
      --size-sub: 1.125rem;
      --weight-sub: 500;

      --btn-font-size: 1rem;
      --btn-padding: 0.875rem;
      --btn-margin: 1rem 0 0 0;

      height: 0vh;
      padding: 0;
      top: 50%;
      transform: translate(0, -50%);
      --box-shadow: 0 0 0 1px var(--clr-black-3), 0px 0px 8rem 1rem black;
    "
    data-tst="study_messages"
  >
    <div
      v-if="messages.includes('loading') || messages.includes('loading_custom_reports')"
      class="ma-auto f-column f-center position-absolute"
      style="top: 0; left: 0; right: 0; bottom: 0; margin: auto"
      data-tst="study_loader"
    >
      <LoaderSpinner
        :style="`--size:4rem`"
        style="min-height: 3.5rem"
      />
      <LoadingTip
        v-if="showLoadingTips"
        class="anim-fade-in_super-slow mt-4"
        :fixedTip="
          messages.includes('loading_custom_reports') ? $t('messages.loading_custom_reports') : ''
        "
      />
    </div>
    <MessagesGoPostflop
      v-if="messages.includes('postflop_equity_only')"
      :namespace="namespace"
      :canAISolve="canAISolve"
      :hasShortcutBtn="hasShortcutBtn"
      :posflopContent="$t(`tabs.${$store.getters.sw_tabs_study}`)"
      data-tst="postflop_equity_only"
    />
    <div
      v-if="messages.includes('select_board_strategy')"
      class="gw_mesblock"
      data-tst="select_board_strategy"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.select_board_strategy.title`)"
      />
      <div
        id="selectboardbtn"
        class="gw_btn-v1"
        :data-tst="`btn_select_board_strategy`"
        style="font-size: var(--btn-font-size); margin: var(--btn-margin)"
        @click="
          $store.dispatch(
            'router_set_dialogs',
            `cards-dialog${namespace ? `_namespace-${namespace}` : ''}`,
          )
        "
      >
        <i class="mdi mdi-cards-playing text-large mr-2"></i>
        {{ $t('btn.select_board') }}
      </div>
    </div>
    <div
      v-if="messages.includes('custom_reports_payment_required')"
      class="gw_mesblock"
      style="padding: 1.5rem; align-items: start"
      data-tst="custom_reports_payment_required"
    >
      <div
        class="gw_mesblock_title mb-2"
        style="font-size: 1.5rem"
      >
        {{ $t('messages.custom_reports_payment_required.title') }}
      </div>

      <div
        v-if="$store.getters.rep_data_report_insufficient_balance"
        class="gw_mesblock_subtitle custom_reports_payment_required_subtitle"
      >
        <div class="d-flex">
          {{ $t(`messages.custom_reports_payment_required.report_cost`) }}
          <div
            class="d-flex"
            style="color: var(--clr-primary); align-items: center"
          >
            <Chip class="custom_reports_payment_required_icon" />
            {{ reportPrice }}
          </div>
        </div>
        <div
          class="d-flex"
          style="color: var(--clr-pink); align-items: center"
        >
          {{ $t(`messages.custom_reports_payment_required.insufficient_balance`) }}
          <Chip class="custom_reports_payment_required_icon" />
          {{ creditsBalance }}
        </div>
      </div>
      <div
        v-else
        class="gw_mesblock_subtitle custom_reports_payment_required_subtitle"
      >
        <div class="d-flex">
          {{ $t(`messages.custom_reports_payment_required.report_cost`) }}
          <div
            class="d-flex"
            style="color: var(--clr-primary); align-items: center"
          >
            <Chip class="custom_reports_payment_required_icon" />
            {{ reportPrice }}
          </div>
        </div>
        <div
          class="d-flex"
          style="align-items: center"
        >
          <div style="opacity: 0.7">
            {{ $t(`messages.custom_reports_payment_required.current_balance`) }}
          </div>
          <Chip class="custom_reports_payment_required_icon" />
          {{ creditsBalance }}
        </div>
      </div>

      <div
        class="d-flex flex-row mt-2"
        style="gap: 1.5rem; justify-content: center; width: 100%"
      >
        <GwBtn
          variant="btn_text_white"
          class="gw_btn custom_reports_payment_required_btn"
          :data-tst="`btn_show_reports_history`"
          @click="$emit('click:show-reports-history')"
        >
          {{ $t('messages.custom_reports_payment_required.report_history') }}
        </GwBtn>

        <GwBtn
          v-if="!$store.getters.rep_data_report_insufficient_balance"
          variant="btn_primary"
          class="custom_reports_payment_required_btn"
          :data-tst="`btn_generate_report`"
          @click="$emit('click:generate-report')"
        >
          {{ $t('messages.custom_reports_payment_required.generate_report') }}
        </GwBtn>
        <router-link
          v-else
          :to="{ name: 'UserCredits' }"
        >
          <GwBtn
            variant="btn_primary"
            class="custom_reports_payment_required_btn"
            :data-tst="`btn_generate_report`"
          >
            {{ $t('messages.custom_reports_payment_required.get_more_credits') }}
          </GwBtn></router-link
        >
      </div>
    </div>
    <div
      v-if="messages.includes('no_custom_sol_reports')"
      class="gw_mesblock"
      data-tst="no_custom_sol_reports"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.no_custom_sol_reports.title`)"
      />
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.no_custom_sol_reports.subtitle`)"
      ></div>
      <div
        class="d-flex mx-auto"
        style="gap: 0.5rem"
      >
        <div
          class="gw_btn-v1"
          style="
            font-size: var(--btn-font-size);
            margin: var(--btn-margin);
            --clr-back: var(--clr-blue-1);
          "
          @click="switchToSolutionLibrary()"
        >
          {{ $t('messages.no_custom_sol_reports.switch_to_library_solutions') }}
        </div>
      </div>
    </div>
    <div
      v-if="messages.includes('no_river')"
      class="gw_mesblock"
      data-tst="no_river"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.no_river.title`)"
      />
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.no_river.subtitle`)"
      ></div>
      <div
        class="d-flex mx-auto"
        style="gap: 0.5rem"
      >
        <div
          class="gw_btn-v1"
          style="
            font-size: var(--btn-font-size);
            margin: var(--btn-margin);
            --clr-back: var(--clr-blue-1);
          "
          @click="goToStreet('flop')"
        >
          {{ $t('table_head.flops') }}
        </div>
        <div
          class="gw_btn-v1"
          style="
            font-size: var(--btn-font-size);
            margin: var(--btn-margin);
            --clr-back: var(--clr-blue-1);
          "
          @click="goToStreet('turn')"
        >
          {{ $t('table_head.turns') }}
        </div>
      </div>
    </div>
    <div
      v-if="messages.includes('no_custom_report_turn')"
      class="gw_mesblock"
      data-tst="no_custom_report_turn"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.no_custom_report_turn.title`)"
      />
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.no_custom_report_turn.subtitle`)"
      ></div>
      <div
        class="d-flex mx-auto"
        style="gap: 0.5rem"
      >
        <div
          class="gw_btn-v1"
          style="
            font-size: var(--btn-font-size);
            margin: var(--btn-margin);
            --clr-back: var(--clr-blue-1);
          "
          @click="goToFlop()"
        >
          {{ $t('table_head.flops') }}
        </div>
      </div>
    </div>
    <div
      v-if="messages.includes('turnZeroDataValues')"
      class="gw_mesblock"
      data-tst="turnZeroDataValues"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.turnZeroDataValues.title`)"
      />
    </div>
    <ReportsAvailableShortcuts
      v-if="messages.includes('trialUser403')"
      data-tst="trialUser403"
      userCode="trialUser403"
      :namespace="''"
    />
    <ReportsAvailableShortcuts
      v-if="messages.includes('starterUser403')"
      data-tst="starterUser403"
      userCode="starterUser403"
      :namespace="''"
    />
    <div
      v-if="messages.includes('FREE_unavailable')"
      class="gw_mesblock"
      data-tst="FREE_unavailable"
    >
      <div
        class="gw_mesblock_title"
        v-html="
          $t(`messages.FREE_unavailable.title`, {
            game: gameformatTra,
          })
        "
      />
      <a
        class="gw_mesblock_subtitle f-center mt-4 clr-white"
        href="https://youtu.be/D1vzP_nj77I"
        target="_blank"
      >
        <i class="mdi mdi-play-circle-outline text-large mr-2"></i>
        <span
          class="text-underline"
          v-html="$t(`messages.FREE_unavailable.video_title`)"
        >
        </span>
      </a>
      <v-btn
        class="mt-2"
        size="large"
        color="primary"
        :to="{
          name: 'SubscriptionOverview',
          query: { gm_type: gameformat.gm_type },
        }"
        data-tst="study_upgrade_btn"
      >
        <i class="mdi text-large mdi-crown mr-2"></i>
        {{ $t('btn.upgrade') }}
      </v-btn>
    </div>
    <div
      v-if="messages.includes('filtered_204')"
      class="gw_mesblock"
      data-tst="filtered_204"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t('data_table.reports_table.no_data')"
      ></div>
    </div>

    <div
      v-if="messages.includes('204')"
      class="gw_mesblock"
      data-tst="204"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.${pageKey}.title_204`, gmfMessageProps)"
      ></div>
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.${pageKey}.subtitle_204`, gmfMessageProps)"
      ></div>

      <div
        v-if="hasAiPrefillBtn"
        class="mx-auto mt-4 bt-grey pt-4 w-100"
      >
        <BtnPrefillAi @click:prefill="$emit('click:prefill')">
          <div
            class="gw_btn gw_btn_primary"
            style="--padding: 0 0.5rem"
            data-tst="btn_prefill_message"
          >
            <i class="text-medium mdi mdi-auto-fix mr-1"></i>
            <span class="text-medium weight_500">
              {{ $t(`solfigr.ai_solve_report`) }}
            </span>
          </div>
        </BtnPrefillAi>
      </div>
    </div>
    <div
      v-if="messages.includes('204_elite')"
      class="gw_mesblock"
      data-tst="204_elite"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.${pageKey}.title_204_elite`, gmfMessageProps)"
      ></div>
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.${pageKey}.subtitle_204_elite`, gmfMessageProps)"
      ></div>
    </div>

    <div
      v-if="messages.includes('403_only_subscribed')"
      class="gw_mesblock"
      data-tst="403_only_subscribed"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.${pageKey}.title_only_subscribed_403`, gmfMessageProps)"
      ></div>
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.${pageKey}.subtitle_403_tra01`, gmfMessageProps)"
      ></div>
      <div
        v-if="gmfAvailableDepths && starterPurchasedForCurrentGametypeGroup"
        class="starter-available-depths text-center text-normal clr-grey weight_500 pt-2 mt-2 bt-grey"
        v-html="
          gmfAvailableDepths.customMessage
            ? $t(`messages.${pageKey}.subtitle_403_starter_depths_custom`, {
                str: gmfAvailableDepths.customMessage,
                gameformat: gameformatTra,
              })
            : $t(`messages.${pageKey}.subtitle_403_starter_depths`, {
                depths: gmfAvailableDepths.stacks,
                gameformat: gameformatTra,
              })
        "
      />
      <v-btn
        size="large"
        class="mt-4"
        color="primary"
        :to="{
          name: 'SubscriptionOverview',
          query: { gm_type: gameformat.gm_type },
        }"
      >
        <i class="mdi text-large mdi-crown mr-2"></i>
        {{ $t('btn.upgrade') }}
      </v-btn>
    </div>

    <div
      v-if="messages.includes('403')"
      class="gw_mesblock"
      data-tst="403"
    >
      <div
        class="gw_mesblock_title"
        v-html="
          tm_accessInPremiumSubOnly
            ? $t(`messages.${pageKey}.title_only_subscribed_403`)
            : $t(
                `messages.${pageKey}.title${gameformatStarter ? '_starter' : ''}_403`,
                gmfMessageProps,
              )
        "
      ></div>
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.${pageKey}.subtitle_403_tra01`, gmfMessageProps)"
      ></div>
      <div
        v-if="gmfAvailableDepths && starterPurchasedForCurrentGametypeGroup"
        class="starter-available-depths text-center text-normal clr-grey weight_500 pt-2 mt-2 bt-grey"
        v-html="
          gmfAvailableDepths.customMessage
            ? $t(`messages.${pageKey}.subtitle_403_starter_depths_custom`, {
                str: gmfAvailableDepths.customMessage,
                gameformat: gameformatTra,
              })
            : $t(`messages.${pageKey}.subtitle_403_starter_depths`, {
                depths: gmfAvailableDepths.stacks,
                gameformat: gameformatTra,
              })
        "
      />
      <v-btn
        size="large"
        class="mt-4"
        color="primary"
        :to="{
          name: 'SubscriptionOverview',
          query: { gm_type: gameformat.gm_type },
        }"
      >
        <i class="mdi text-large mdi-crown mr-2"></i>
        {{ $t('btn.upgrade') }}
      </v-btn>
    </div>
    <div
      v-if="messages.includes('429')"
      class="gw_mesblock"
      data-tst="429"
    >
      <div
        class="gw_mesblock_title"
        v-html="
          $t(`messages.${pageKey}.title${gameformatStarter ? '_starter' : ''}_429`, gmfMessageProps)
        "
      ></div>
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.${pageKey}.subtitle_429`, gmfMessageProps)"
      ></div>
    </div>
    <div
      v-if="messages.includes('preflopOnlyGametype')"
      class="gw_mesblock"
      data-tst="preflopOnlyGametype"
    >
      <!-- <div class="div" v-html="gmfMessageProps.gameformat_option"></div> -->
      <div
        class="gw_mesblock_title"
        v-html="
          $t(`messages.game_format_unavailable.title_tra02`, {
            gametype: gmfMessageProps.gameformat_option,
          })
        "
      ></div>

      <BtnPrefillAi
        v-if="canAISolve && gameformat.subscription_tier"
        id="btn_prefill"
        @click:prefill="$emit('click:prefill')"
      >
        <div
          class="gw_btn gw_btn_primary mt-4 mx-auto"
          style="--height: 2.5rem; --padding: 1rem"
        >
          <i class="mdi mdi-auto-fix mr-2"></i>
          <span class="weight_500">
            {{ $t(`solfigr.solve_postflop_ai`) }}
          </span>
        </div>
      </BtnPrefillAi>
      <router-link
        v-else-if="canAISolve && !gameformat.subscription_tier"
        class="gw_btn gw_btn_primary mt-4 mx-auto"
        style="--height: 2.5rem; --padding: 1rem"
        :to="{
          name: 'SubscriptionOverview',
          query: { gm_type: 'elite', gm_type: gameformat.gm_type },
        }"
      >
        <i class="mdi mdi-crown mr-2"></i>
        <span class="weight_500">
          {{ $t('btn.upgrade_to_ai_solving') }}
        </span>
      </router-link>
      <div
        v-else
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.game_format_unavailable.subtitle`)"
      ></div>
      <div class="clr-grey weight_500 f-column f-center mt-6 w-100">
        <div
          v-if="gameformat.group === 'mtt'"
          class="pt-4 bt-grey f-column f-center w-100"
        >
          {{ $t('messages.available_posftlop_mtt') }}
          <div
            class="gw_btn mt-3 mx-auto"
            style="--clr-back: var(--clr-blue-1); --height: 2.5rem; --padding: 1rem"
            @click="switchToMttChipev()"
          >
            {{ $t('messages.switch_to_mtt_chipev') }}
          </div>
        </div>

        <div
          v-else
          class="f-column f-center pt-4 bt-grey w-100"
        >
          {{ $t('gameformat.select_diff_format') }}:
          <div class="d-flex">
            <GameFormatSelector
              :hasArrow="false"
              :namespace="namespace"
              :minimized="true"
              :hasBubble="false"
              :solElite="solElite"
              class="mx-auto"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="messages.includes('error_loading_solution')"
      class="gw_mesblock"
      data-tst="error_loading_solution"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.errors.error_loading_solution`)"
      ></div>

      <div class="clr-grey pt-4 text-center bt-grey f-center f-column mt-4">
        <span v-html="$t('messages.if_problem_contact_us_with_url')"> </span>
        <GwBtnClipboard
          class="flex-1 mt-2"
          copyUrl
        />
      </div>
    </div>

    <div
      v-if="messages.includes('error_400_TREE_IS_TOO_BIG')"
      class="gw_mesblock"
      data-tst="error_400_TREE_IS_TOO_BIG"
    >
      <div
        class="gw_mesblock_title"
        v-html="$t(`messages.errors.title_400_TREE_IS_TOO_BIG`)"
      ></div>
      <div
        class="gw_mesblock_subtitle"
        v-html="$t(`messages.errors.subtitle_400_TREE_IS_TOO_BIG`)"
      ></div>

      <div
        class="gw_btn-v1"
        style="
          font-size: var(--btn-font-size);
          margin: var(--btn-margin);
          --clr-back: var(--clr-blue-1);
        "
        @click="openCustomSolutionsDialog()"
      >
        <i class="text-normal-large mdi mdi-pencil mr-1"></i>
        {{ $t('btn.edit_solution') }}
      </div>

      <div class="clr-grey text-center pt-4 bt-grey f-center f-column mt-4">
        <span
          style="max-width: 22rem"
          v-html="$t('messages.if_problem_contact_us_with_url')"
        >
        </span>
        <GwBtnClipboard
          class="flex-1 mt-2"
          copyUrl
        />
      </div>
    </div>

    <div
      v-if="messages.includes('spins_reports_basic_only')"
      class="gw_mesblock"
      data-tst="spins_reports_basic_only"
    >
      <div class="gw_mesblock_title">
        Aggregated reports are currently only available for <br />
        <span
          class="clr-blue link-underline link-underline_hover cursor-pointer"
          @click="switchToBasicSpinsGmf()"
          >Basic Spin & Go</span
        >
        solutions.
      </div>
      <div class="gw_mesblock_subtitle">
        We're working on adding reports for all situations soon. Thanks for your patience.
      </div>

      <div
        class="gw_btn-v1"
        style="
          font-size: var(--btn-font-size);
          margin: var(--btn-margin);
          --clr-back: var(--clr-blue-1);
        "
        @click="switchToBasicSpinsGmf()"
      >
        Select Basic Spin & Go
      </div>
    </div>

    <div
      v-if="
        [
          'custom_sols_flop_elite_only',
          'custom_sols_preflop_elite_only',
          'custom_sols_flop_elite_with_rake_only',
          'custom_sols_preflop_elite_with_rake_only',
          'custom_sols_elite_only',
        ].some(i => messages.includes(i))
      "
      class="gw_mesblock"
      data-tst="custom_sols_elite_only"
      :data-message="messages[0]"
    >
      <div
        class="gw_mesblock_title"
        data-tst="upgrade_to_elite_unlock_custom_solutions"
        v-html="getCussolMessage()"
      ></div>

      <router-link
        class="gw_btn gw_btn-v1 w-100 gw_btn_primary weight_500 mt-6 clr-white"
        style="--padding: 1.5rem; font-size: 1rem"
        :to="{
          name: 'SubscriptionOverview',
          query: { gm_type: 'elite' },
        }"
      >
        <i class="mdi text-large mdi-crown mr-2"></i>
        {{ $t('btn.go_elite') }}
      </router-link>

      <div
        v-if="
          [
            'custom_sols_flop_elite_only', //
            'custom_sols_flop_elite_with_rake_only',
          ].some(i => messages.includes(i))
        "
        class="d-flex f-column w-100 bt-greys mt-2 mx-auto"
        style="gap: 0.5rem"
      >
        <div
          class="gw_btn-v1 w-100 clr-white f-column"
          style="font-size: 1rem; --padding: 1.5rem; --brighten-active: 0.05"
          @click="switchToFreeEliteBoard()"
        >
          <div class="f-center">
            Try free flop
            <CardRowSimple
              class="ml-2"
              :board="'QsTs7h'"
              style="--crds-card-height: 1.875rem; --crds-card-value-size-increase: 0.9"
            >
            </CardRowSimple>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="messages.includes('forced_fold')"
      class="gw_mesblock"
      data-message="forced_fold"
    >
      <div class="gw_mesblock_title">{{ $t('messages.forced_fold_title') }}</div>
      <div class="gw_mesblock_subtitle">
        {{ $t('messages.forced_fold_subtitle') }}
      </div>
    </div>
    <div
      v-if="messages.includes('custom_reports_on_demand')"
      class="gw_mesblock"
      data-message="forced_fold"
    >
      <div class="gw_mesblock_title">Aggregated reports for custom solutions</div>
      <div class="gw_mesblock_subtitle">Reports can be generated for credits</div>
      <div
        id="selectboardbtn"
        class="gw_btn-v1"
        :data-tst="`btn_select_board_strategy`"
        style="font-size: var(--btn-font-size); margin: var(--btn-margin)"
        @click="$store.dispatch('router_set_dialogs', 'generate-custom-reports-dialog')"
      >
        <i class="mdi mdi-cards-playing text-large mr-2"></i>
        Generate report
      </div>
    </div>
  </MessageOverlaySkeleton>
</template>

<script>
import ReportsAvailableShortcuts from '@/components/Poker/Overlays/ReportsAvailableShortcuts'
import GameFormatSelector from '@/components/GameFormat/GameFormatSelector'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner'
import CardRowSimple from '@/components/CardRow/CardRowSimple'
import BtnPrefillAi from '@/components/SpecialBtns/BtnPrefillAi'
import GwBtnClipboard from '@/components/lib/GwBtnClipboard/GwBtnClipboard'
import tier_mix from '@/mixins/tier_mix'
import LoadingTip from '@/components/Poker/Overlays/LoadingTip'
import format_mix from '@/mixins/format_mix'
import { getGameformatOptionHtml } from '@/store/gameformat/gameformat'
import { SNK } from '@/helpers/helpers'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import Chip from '@/assets/icons/chip.svg'

import MessageOverlaySkeleton from '@/components/Poker/Overlays/MessageOverlaySkeleton'
import MessagesGoPostflop from '@/components/Poker/Overlays/Messages/MessagesGoPostflop'

export default {
  name: 'StudyMessageOverlay',
  components: {
    LoadingTip,
    MessagesGoPostflop,
    MessageOverlaySkeleton,
    ReportsAvailableShortcuts,
    GameFormatSelector,
    LoaderSpinner,
    CardRowSimple,
    BtnPrefillAi,
    GwBtnClipboard,
    GwBtn,
    Chip,
  },
  mixins: [format_mix, tier_mix],
  props: {
    namespace: String,
    canAISolve: { required: true, type: Boolean },
    solElite: Object,
    hasAiPrefillBtn: Boolean,
    messages: {
      default: () => [
        'loading',
        'postflop_equity_only',

        'custom_sols_preflop_elite_only',
        'custom_sols_flop_elite_only',
        'custom_sols_flop_elite_with_rake_only',
        'custom_sols_preflop_elite_with_rake_only',
        'custom_sols_elite_only',

        'select_board_strategy',
        'turnZeroDataValues',
        'no_river',
        'no_custom_sol_reports',
        'trialUser403',
        'starterUser403',
        'FREE_unavailable',
        'preflopOnlyGametype',
        'filtered_204',
        '204',
        '204_elite',
        '403_only_subscribed',
        '403',
        '429',
        // 'custom_sols_elite_create',
        'forced_fold',

        'error_loading_solution',
        'error_400_TREE_IS_TOO_BIG',

        'loading_custom_reports',
        'custom_reports_on_demand',
        // loading
        // postflop_equity_only
        // select_board_strategy
        // no_river
        // turnZeroDataValues
        // trialUser403
        // starterUser403
        // FREE_unavailable
        // filtered_204
        // 204
        // 403
        // 429
        // preflopOnlyGametype
        // error_loading_solution
        // spins_reports_basic_only
        // loading
      ],
    },
    mobile: Boolean,
    hasShortcutBtn: { type: Boolean, default: true },
    street: { default: 'preflop' },
    // gameformat: { default: () => ({}) },
    pageKey: { default: 'solutions' },
    historySpots: Array,
    hasCustomReportsHistory: Boolean,
  },
  emits: ['click:prefill', 'click:generate-report', 'click:show-reports-history'],
  data() {
    return {}
  },
  methods: {
    getCussolMessage() {
      return (
        {
          custom_sols_preflop_elite_only: this.$t(
            `messages.upgrade_to_elite_unlock_custom_preflop_solutions`,
          ),
          custom_sols_flop_elite_only: this.$t(`messages.upgrade_to_elite_unlock_custom_solutions`),
          custom_sols_flop_elite_with_rake_only: this.$t(
            `messages.upgrade_to_elite_unlock_custom_solutions_with_rake`,
            { tier: 'Cash or Straddle Ante Cash' },
          ),
          custom_sols_preflop_elite_with_rake_only: this.$t(
            `messages.upgrade_to_elite_unlock_custom_solutions_with_rake`,
            { tier: 'Cash or Straddle Ante Cash' },
          ),
          custom_sols_elite_only: this.$t(`messages.upgrade_to_elite_unlock_custom_solutions`),
        }[this.messages[0]] || this.$t(`messages.upgrade_to_elite_unlock_custom_solutions`)
      )
    },
    openCustomSolutionsDialog() {
      // this.$store.dispatch('sol_openFormDialog')
      const tmpNamespace = 'tmp/primary'
      const dialog = `solfigr-create-custom-solution-dialog_namespace-${tmpNamespace}_mode-keepboard`
      this.$store.dispatch('router_set_dialogs', dialog)
    },
    switchToFreeEliteBoard() {
      const board = 'QsTs7h'
      this.$store.dispatch('router_update_query', {
        namespace: '',
        query: {
          board,
        },
        push: false,
        replace: false,
        saveLocally: false,
      })
      this.$store.dispatch('sol_cust_handleBoardChange', {
        board,
        solElite: this.solElite,
      })
    },
    goToStreet(street) {
      const { history_spots, history_spot } = this.$store.getters
      let hs = history_spot.id
      if (this.street !== street) {
        const firstStreetSpot = history_spots.find(spot => spot.street === street)
        hs = firstStreetSpot.id || hs
      }

      this.$store.dispatch('router_update_query', {
        query: {
          history_spot: hs,
        },
        push: true,
      })
    },
    goToFlop() {
      const { history_spots } = this.$store.getters

      const firstStreetSpot = history_spots.find(spot => spot.street === 'flop')
      const hs = firstStreetSpot.id || 0

      this.$store.dispatch(`gamestate_set_history`, {
        history_spots,
        spot_id: hs,
      })
    },
    switchToBasicSpinsGmf() {
      const gameformat = this.$store.getters.gameformats.find(
        i => i.group === 'spins' && i.type === 'basic',
      )
      this.switchGmf(gameformat)
    },
    switchToMttChipev() {
      const gameformat = this.$store.getters.gameformats.find(
        i => i.group === 'mtt' && !i.preflop_only && i.players === 8,
      )
      this.switchGmf(gameformat)
    },
    switchGmf(gameformat) {
      if (!gameformat) return
      const channelId = this.$store.getters.bc_channels_ids.find(id => id.includes('floating_gmf'))
      this.$store.dispatch('globals_action', 'disable_history_watcher')
      setTimeout(() => {
        this.$store.dispatch('gmfs_set_gameformat', {
          gameformat,
          instaApply: true,
          routerParams: {
            push: false,
            replace: true,
            saveLocally: true,
          },
          keepHistoryTree: true,
          hasLists: false,
          channelId,
        })
      })
    },
    switchToSolutionLibrary() {
      this.$store.dispatch('router_update_query', {
        query: {
          solution_type: 'gwiz',
        },
        push: true,
        replace: false,
        saveLocally: true,
      })
      this.$store.dispatch('api_poker_history', { namespace: '' })
    },
  },
  computed: {
    reportPrice() {
      return this.$store.getters.rep_data_customReportMeta.price
    },
    creditsBalance() {
      return this.$store.getters.user_info.credit_balance
    },
    showLoadingTips() {
      return this.$store.getters.mergedUserSettings.showLoadingTips
    },
    // street() { return this.$store.getters.street },
    // gameformat() { return this.$store.getters.gameformat },
    gameformat() {
      const res = this.$store.getters[SNK('gameformat', this.namespace)]
      return res
    },
    gmfAvailableDepths() {
      const gmf = this.gameformat
      if (gmf?.group === 'cash') {
        return { stacks: '100' }
      }
      if (gmf?.group === 'mtt') {
        return { stacks: '14, 25, 35, 60, 80' }
      }
      if (gmf?.group === 'spins') {
        return { customMessage: 'Symmetric solutions (all players have the same stacks)' }
      }
      if (gmf?.group === 'husng') {
        return { stacks: '25, 20, 18, 15, 12, 10, 8, 6, 5, 4, 3, 2, 1' }
      }
      return null
    },
    gmfMessageProps() {
      return {
        gameformat: this.gameformatTra,
        gameformat_option: getGameformatOptionHtml({ gameformat: this.gameformat }),
        gameformat_tiers:
          this.unpurchasedGameformatTiersTra !== this.gameformatTra
            ? this.unpurchasedGameformatTiersTra
            : '', // if both gameformat and gameformat_tiers are the same string only show one
      }
    },
    gameformatTra() {
      const localizeKey = this.gameformat?.cash_type?.includes('straddle')
        ? 'straddle_ante_cash'
        : this.gameformat.gm_type
      return this.$t(`gametypes.groups.${localizeKey}_long`)
    },
    unpurchasedGameformatTiersTra() {
      const res = `${
        this.gameformat?.format?.includes('straddle') ||
        this.gameformat?.cash_type?.includes('straddle')
          ? 'Straddle+Ante'
          : this.gameformatAny || this.gameformatTrial
            ? `${this.$t(`tier.options.starter`)} / ${this.$t(`tier.options.premium`)}`
            : this.$t(`tier.options.premium`)
      }`
      return res
    },
  },
}
</script>

<style lang="scss" scoped>
.custom_reports_payment_required_subtitle {
  font-weight: 400;
  color: var(--clr-white);
  font-size: 1.2rem;
  line-height: 1.8;
  text-align: left;
}

.custom_reports_payment_required_icon {
  height: 1.2rem;
  width: 1.2rem;
  margin: 0 0.3rem 0 0.5rem;
}

.custom_reports_payment_required_btn {
  font-size: 1.2rem;
  margin: var(--btn-margin);
  padding: 1.7rem;
}
</style>
